import { Box, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import Content from '../components/Layout/Content';
import { VitalChart } from '../components/VitalChart/VitalChart';
import { VitalDataTiles } from '../components/VitalDataTiles/VitalDataTiles';
import { dateFilterMenuItems, VITALS_LABELS } from '../shared/constants';
import { dateParse, handleTimeFilter } from '../shared/helper';
import { VITALS } from '../shared/Strings';

const graphContainer = {
  background: '#fff',
  p: '20px 10px 10px 0px',
  mt: 4,
  width: '100%',
  mb: 4,
  boxShadow: '2px 2px 24px rgba(75, 75, 75, 0.08)',
  borderRadius: '16px',
};

const lastCardWrapper = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  maxWidth: '584px',
  minWidth: '200px',
  backgroundColor: '#FFFFFF',
  border: '1px solid #E4E4E4',
  boxSizing: 'border-box',
  borderRadius: '8px',
  padding: '20px',
  marginTop: '4px',
  cursor: 'pointer',
};

export const VitalDetails = props => {
  const { vitalType } = useParams();
  const { state } = useLocation();
  const allData = useMemo(() => state?.data ?? [], [state]);
  const isBpGraph = state?.isBpGraph ?? false;
  const componentTitle = VITALS_LABELS[vitalType];
  const navigate = useNavigate();

  const breadcrumbData = [
    { label: 'Medical', route: '/medical' },
    { label: 'Vitals', route: '/medical/vitals' },
    { label: VITALS_LABELS[vitalType] },
  ];

  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState(dateFilterMenuItems[0].value);
  const [vitalTilesData, setVitalTilesData] = useState([]);

  const allDataComponentData = [...allData].sort(
    (a, b) => Date.parse(b.date) - Date.parse(a.date)
  );
  const [dateFilterText, setDateFilterText] = useState('');

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      const filterVal = ev.target.value;
      setTimeFilter(filterVal);
      setDateFilterText(
        constructDateFilterText(Number(filterVal.split(' ')[0]))
      );
      const filterResData = handleTimeFilter(filterVal, allData);
      setFilteredData(filterResData);
      const tilesData =
        filterResData &&
        [...filterResData]
          .sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
          .slice(0, 3);
      setVitalTilesData(tilesData);
    },
  };

  const constructDateFilterText = days => {
    if (!days) {
      return '';
    }
    const date = new Date();
    date.setDate(date.getDate() - days);
    return `${dateParse(date)} - ${dateParse(Date())}`;
  };

  const handleShowAllBtnClick = () => {
    navigate(`/medical/vitals/${vitalType}/alldata`, {
      state: {
        data: allDataComponentData,
        ...state,
      },
    });
  };

  useEffect(() => {
    if (allData) {
      const filterVal = dateFilterMenuItems[0].value;
      setTimeFilter(filterVal);
      setDateFilterText(
        constructDateFilterText(Number(filterVal.split(' ')[0]))
      );
      const filterResData = handleTimeFilter(filterVal, allData);
      setFilteredData(filterResData);
      const tilesData =
        filterResData &&
        [...filterResData]
          .sort((a, b) => Date.parse(b.date) - Date.parse(a.date))
          .slice(0, 3);
      setVitalTilesData(tilesData);
    }
  }, [state, allData]);

  if (!state) {
    return <Navigate to={'/medical/vitals'} />;
  }

  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title">{componentTitle}</Typography>
      <Box sx={{ display: 'flex', position: 'relative', mt: 2 }}>
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
      </Box>
      {filteredData.length > 0 && (
        <Box sx={graphContainer}>
          <VitalChart
            allData={allData}
            filteredData={filteredData}
            isBpGraph={isBpGraph}
            dateFilterText={dateFilterText}
          />
        </Box>
      )}
      <Box>
        {!vitalTilesData.length ? (
          <h4>{VITALS.NO_DATA_FOUND}</h4>
        ) : (
          vitalTilesData.map((cardData, i) => {
            return (
              <VitalDataTiles data={cardData} isBpGraph={isBpGraph} key={i} />
            );
          })
        )}
        {filteredData.length > 3 && (
          <Box sx={lastCardWrapper} onClick={handleShowAllBtnClick}>
            <Typography display="inline" variant="cardTitle">
              {VITALS.SHOW_ALL_TEXT}
            </Typography>
          </Box>
        )}
      </Box>
    </Content>
  );
};
